import React, {useCallback, useRef} from 'react';

import {isSupplementAnchor} from '../engine/annotationAnchors';
import {
  describeMarkRemoved,
  describeNoteAdded,
  describeNoteRemoved,
  describeStyleChange
} from '../shared/annotationAnnouncements';
import {announceMarkChange} from '../shared/announceMarkChange';
import {focusAnnotationFallbackIfLost} from '../shared/annotationFocusFallback';
import AnnotationPopover from './AnnotationPopover';
import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {selectAnnotation, selectIsNoteListed} from '../store/annotationSelectors';
import type {AnnotationAnchor} from '../types';

interface AnnotationEditorProps {
  regionId: string;
  /** The mark being edited and the viewport rect its popover hangs from. */
  activeAnnotation: AnnotationAnchor;
  /** A live box to hang from instead of that rect. */
  anchorEl?: HTMLElement | null;
  onClose: () => void;
  /** False where a mark cannot be an underline (a whole-supplement border). */
  allowUnderline?: boolean;
  /** Opened from a note's pencil: focus starts in the note field. */
  focusNote?: boolean;
}

/**
 * Store-connected editor for one active mark. Keyed by the mark, so switching marks
 * starts over with that mark's note draft and what has been said about it.
 */
export default function AnnotationEditor(props: AnnotationEditorProps) {
  return <MarkEditor key={props.activeAnnotation.id} {...props} />;
}

function MarkEditor({
  regionId,
  activeAnnotation,
  anchorEl,
  onClose,
  allowUnderline = true,
  focusNote = false
}: AnnotationEditorProps) {
  const setHighlightColor = useAnnotationModeStore((state) => state.setHighlightColor);
  const setAnnotationUnderline = useAnnotationModeStore((state) => state.setAnnotationUnderline);
  const setAnnotationNote = useAnnotationModeStore((state) => state.setAnnotationNote);
  const removeAnnotation = useAnnotationModeStore((state) => state.removeAnnotation);
  const annotationId = activeAnnotation.id;
  const mark = useAnnotationModeStore((state) => selectAnnotation(state, regionId, annotationId));
  // Not read from the store, which holds the note from the first keystroke, before it is spoken.
  const isNoteKnownRef = useRef(mark?.note !== undefined);

  const handleNoteChange = useCallback(
    (nextNote: string) => {
      if (nextNote.trim()) {
        setAnnotationNote(regionId, annotationId, nextNote);
        return;
      }
      if (!selectIsNoteListed(useAnnotationModeStore.getState(), regionId, annotationId)) {
        return;
      }
      const focused = document.activeElement;
      setAnnotationNote(regionId, annotationId, '');
      // Deferred past the commit: the focused control may go with the note (a margin indicator does).
      setTimeout(() => {
        if (focused && !focused.isConnected) {
          focusAnnotationFallbackIfLost();
        }
      });
      if (isNoteKnownRef.current) {
        announceMarkChange(regionId, annotationId, describeNoteRemoved);
      }
    },
    [regionId, annotationId, setAnnotationNote]
  );

  const handleNoteCommit = useCallback(() => {
    if (
      isNoteKnownRef.current ||
      !selectIsNoteListed(useAnnotationModeStore.getState(), regionId, annotationId)
    ) {
      return;
    }
    isNoteKnownRef.current = true;
    announceMarkChange(regionId, annotationId, describeNoteAdded);
  }, [regionId, annotationId]);

  if (!mark) {
    return null;
  }

  const announceStyleChange = () =>
    announceMarkChange(regionId, annotationId, (after, subject) =>
      describeStyleChange(mark, after, subject)
    );

  return (
    <AnnotationPopover
      anchorRect={activeAnnotation.rect}
      anchorEl={anchorEl}
      selectedColor={mark.color}
      selectedType={mark.type}
      note={mark.note ?? ''}
      noteLabel={
        isSupplementAnchor(mark.anchor)
          ? 'Note on this supplement'
          : `Note on “${mark.anchor.exact}”`
      }
      focusNote={focusNote}
      allowUnderline={allowUnderline}
      onSelectColor={(nextColor) => {
        setHighlightColor(regionId, annotationId, nextColor);
        announceStyleChange();
      }}
      onSelectUnderline={() => {
        setAnnotationUnderline(regionId, annotationId);
        announceStyleChange();
      }}
      onNoteChange={handleNoteChange}
      onNoteCommit={handleNoteCommit}
      onRemove={() => {
        announceMarkChange(regionId, annotationId, describeMarkRemoved);
        removeAnnotation(regionId, annotationId);
        onClose();
      }}
      onDismiss={onClose}
    />
  );
}
